import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { DATA_TABLE_SOURCE_UTILITY_MAPPING_DEFAULTS } from 'common/dataTableConstants';

import { SR_API_ACTION_UTILITY_PARTNER_ADDRESS_DELETE_MAPPING_DEFAULT } from 'api/utilityActionTypes';

import { actionDataTableRemoveItem } from 'client/helpers/dataTables';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrAdminDropdownButton from 'client/components/UPAdminDropdownButton/UPAdminDropdownButton';
import UPSearchBar from 'client/components/UPSearchBar/UPSearchBar';

const UPDataTableMappingDefaults = ({ utilityTypes }) => {
  const [selectedUtilityTypes, setSelectedUtilityTypes] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const dispatch = useDispatch();

  const getSelectedUtilityTypesText = () => {
    if (selectedUtilityTypes.length === 0) {
      return 'Select Utility Types';
    }

    if (selectedUtilityTypes.length === utilityTypes.length) {
      return 'All';
    }

    if (selectedUtilityTypes.length < 3) {
      return selectedUtilityTypes
        .map((type) => {
          const utilityType = utilityTypes.find((t) => t.id === type);
          return utilityType ? utilityType.label : '';
        })
        .join(', ');
    }

    return (
      selectedUtilityTypes
        .slice(0, 2)
        .map((type) => {
          const utilityType = utilityTypes.find((t) => t.id === type);
          return utilityType ? utilityType.label : '';
        })
        .join(', ') + ` +${selectedUtilityTypes.length - 2}`
    );
  };

  return (
    <SrDataTableConnected
      dataSource={DATA_TABLE_SOURCE_UTILITY_MAPPING_DEFAULTS}
      rootViewId={DATA_TABLE_SOURCE_UTILITY_MAPPING_DEFAULTS}
      initialViewSettings={{
        sort: {
          coverageZone: 'asc',
        },
      }}
      count={30}
      itemsPropName="mappingDefaults"
      resetOnUnmount
    >
      {({
        mappingDefaults,
        viewSettings,
        dataTableApiState,
        dataTableIsFullyLoaded,
        onInfiniteScrollWrapperRef,
      }) => {
        const onDeleteDefault = (mappingDefault) => {
          if (!global.confirm('Are you sure you want to delete this mapping default?')) {
            return;
          }
          dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_DELETE_MAPPING_DEFAULT,
            payload: {
              mappingDefaultId: mappingDefault.id,
            },
            onSuccess() {
              dispatch(actionDataTableRemoveItem, {
                dataSource: DATA_TABLE_SOURCE_UTILITY_MAPPING_DEFAULTS,
                entityId: mappingDefault.id,
              });
            },
          });
        };

        return (
          <>
            <SrBox width="100%">
              <SrBox display="flex" flexDirection="row" alignItems="baseline">
                <SrText
                  font="greycliff"
                  weight="500"
                  fontSize="16px"
                  display="block"
                  paddingRight="8px"
                >
                  Filters:
                </SrText>
                <SrAdminDropdownButton
                  title={
                    selectedUtilityTypes.length === 0
                      ? 'Select Utility Types'
                      : getSelectedUtilityTypesText()
                  }
                  onClickOption={(optionValue) => {
                    const newSelectedUtilityTypes = selectedUtilityTypes.includes(optionValue)
                      ? selectedUtilityTypes.filter((type) => type !== optionValue)
                      : [...selectedUtilityTypes, optionValue];

                    setSelectedUtilityTypes(newSelectedUtilityTypes);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        utilityProviderTypeId: newSelectedUtilityTypes,
                      },
                    });
                  }}
                  options={(utilityTypes || []).map((type) => ({
                    value: type.id,
                    label: type.label,
                    selected: selectedUtilityTypes.includes(type.id),
                  }))}
                  minWidth="200px"
                  maxWidth="250px"
                />
                <UPSearchBar
                  minWidth="300px"
                  maxWidth="350px"
                  height="30px"
                  marginLeft="auto"
                  searchInput={searchInput}
                  placeholder="Search Utility Provider, Partner or Address"
                  onSearchInputChange={(e) => {
                    setSearchInput(e.target.value);
                    viewSettings.update({
                      ...viewSettings,
                      fuzzy:
                        e.target.value.length < 2
                          ? null
                          : {
                              queryString: e.target.value,
                              columns: [
                                'utilityProviderName',
                                'utilityPartnerName',
                                'stateCode',
                                'county',
                                'cityName',
                                'zipCode',
                                'streetDisplay',
                              ],
                            },
                    });
                  }}
                />
              </SrBox>
            </SrBox>
            <SrBox ref={onInfiniteScrollWrapperRef} marginTop="20px">
              <SrDataTableUI
                theme={SR_TABLE_THEME_PARTNER_CLEAR}
                dataTableViewSettings={viewSettings}
                headers={[
                  {
                    content: 'Type',
                    dataTableFieldId: 'utilityProviderType',
                    sortPosition: 'right',
                  },
                  {
                    content: 'Utility Provider',
                    dataTableFieldId: 'utilityProviderName',
                    sortPosition: 'right',
                  },
                  {
                    content: 'Utility Partner',
                    dataTableFieldId: 'utilityPartnerName',
                    sortPosition: 'right',
                  },
                  {
                    content: 'State',
                    dataTableFieldId: 'stateCode',
                    sortPosition: 'right',
                  },
                  {
                    content: 'County',
                    dataTableFieldId: 'county',
                    sortPosition: 'right',
                  },
                  {
                    content: 'City',
                    dataTableFieldId: 'cityName',
                    sortPosition: 'right',
                  },
                  {
                    content: 'Zip',
                    dataTableFieldId: 'zipCode',
                    sortPosition: 'right',
                  },
                  {
                    content: 'Street',
                    dataTableFieldId: 'streetDisplay',
                    sortPosition: 'right',
                  },
                  {
                    content: '# Coverage Points',
                    dataTableFieldId: 'coveragePointsCount',
                    sortPosition: 'right',
                  },
                  { content: 'Exclusive' },
                  { content: 'Actions' },
                ]}
                minWidth="100%"
                rows={mappingDefaults.map((row) => ({
                  cells: [
                    { content: row.utilityProviderType },
                    { content: row.utilityProviderName },
                    {
                      content: row.utilityPartnerName || 'N/A',
                      style: {
                        color: row.utilityPartnerName ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    {
                      content: row.stateCode || 'N/A',
                      style: {
                        color: row.stateCode ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    {
                      content: row.county || 'N/A',
                      style: {
                        color: row.county ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    {
                      content: row.cityName || 'N/A',
                      style: {
                        color: row.cityName ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    {
                      content: row.zipCode || 'N/A',
                      style: {
                        color: row.zipCode ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    {
                      content: row.streetDisplay || 'N/A',
                      style: {
                        color: row.streetDisplay ? '@blue-greycliff-text' : 'grey',
                      },
                    },
                    { content: row.coveragePointsCount },
                    {
                      content: row.isExclusive ? (
                        <SrText
                          font="greycliff"
                          weight="700"
                          fontSize="13px"
                          color="orange300"
                        >
                          Exclusive
                        </SrText>
                      ) : null,
                    },
                    {
                      content: (
                        <SrText
                          font="greycliff"
                          weight="500"
                          fontSize="16px"
                          color="blue300"
                          onClick={() => onDeleteDefault(row)}
                          cursor="pointer"
                        >
                          Delete
                        </SrText>
                      ),
                    },
                  ],
                }))}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
              {mappingDefaults.length === 0 && dataTableIsFullyLoaded && (
                <SrBox
                  backgroundColor="white"
                  display="flex"
                  justifyContent="center"
                  padding="20px"
                >
                  <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                    No defaults added yet.
                  </SrText>
                </SrBox>
              )}
            </SrBox>
          </>
        );
      }}
    </SrDataTableConnected>
  );
};

UPDataTableMappingDefaults.propTypes = {
  utilityTypes: PropTypes.array,
};

UPDataTableMappingDefaults.displayName = 'UPDataTableMappingDefaults';

export default UPDataTableMappingDefaults;
